import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { AdditionalNotesTemplate } from '@/types';
import { supabase } from '@/lib/supabase';
import { generatePrescriptionId } from '@/lib/utils/prescriptionId';
import { getCurrentHospitalId } from '@/lib/hospitalContext';

// Define the store state interface
interface AdditionalNotesTemplatesState {
  templates: AdditionalNotesTemplate[];
  
  // Actions
  addTemplate: (template: Omit<AdditionalNotesTemplate, 'id' | 'createdAt' | 'updatedAt' | 'usageCount'>) => string;
  updateTemplate: (template: AdditionalNotesTemplate) => void;
  removeTemplate: (id: string) => void;
  incrementUsage: (id: string) => void;
  clearTemplates: () => void;
  
  // Selectors
  getTemplatesByDoctor: (doctorId: string) => AdditionalNotesTemplate[];
  getTemplateById: (id: string) => AdditionalNotesTemplate | undefined;
  searchTemplates: (doctorId: string, searchTerm: string) => AdditionalNotesTemplate[];
  
  // Fetch templates from Supabase
  fetchTemplates: (doctorId: string) => Promise<void>;
}

// Create a function to get user-specific storage key
const getStorageKey = () => 'additional-notes-templates-storage';

export const useAdditionalNotesTemplatesStore = create<AdditionalNotesTemplatesState>()(
  persist(
    (set, get) => ({
      templates: [],
      
      // Add a new template
      addTemplate: (template) => {
        const newTemplate: AdditionalNotesTemplate = {
          ...template,
          id: generatePrescriptionId(get().templates),
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          usageCount: 0
        };
        
        set(state => ({
          templates: [...state.templates, newTemplate]
        }));
        
        return newTemplate.id;
      },
      
      // Update an existing template
      updateTemplate: (template) => {
        set(state => ({
          templates: state.templates.map(t => 
            t.id === template.id 
              ? { ...template, updatedAt: new Date().toISOString() }
              : t
          )
        }));
      },
      
      // Remove a template
      removeTemplate: (id) => {
        set(state => ({
          templates: state.templates.filter(t => t.id !== id)
        }));
      },
      
      // Increment usage count
      incrementUsage: (id) => {
        set(state => ({
          templates: state.templates.map(t => 
            t.id === id 
              ? { 
                  ...t, 
                  usageCount: t.usageCount + 1, 
                  lastUsed: new Date().toISOString(),
                  updatedAt: new Date().toISOString()
                }
              : t
          )
        }));
      },
      
      // Get templates by doctor
      getTemplatesByDoctor: (doctorId) => {
        return get().templates.filter(template => template.doctorId === doctorId);
      },
      
      // Get template by ID
      getTemplateById: (id) => {
        return get().templates.find(template => template.id === id);
      },
      
      // Search templates
      searchTemplates: (doctorId, searchTerm) => {
        const term = searchTerm.toLowerCase();
        return get().templates.filter(template => 
          template.doctorId === doctorId &&
          (template.disease.toLowerCase().includes(term) ||
           template.templateName.toLowerCase().includes(term) ||
           template.notes.toLowerCase().includes(term))
        );
      },
      
      // Fetch templates from Supabase
      fetchTemplates: async (doctorId) => {
        try {
          // Get current hospital ID to filter by hospital
          const currentHospitalId = await getCurrentHospitalId();
          if (!currentHospitalId) {
            set({ templates: [] });
            return;
          }

          const { data, error } = await supabase
            .from('additional_notes_templates')
            .select('*')
            .eq('hospital_id', currentHospitalId) // Use hospital_id instead of doctor_id
            .order('created_at', { ascending: false });
            
          if (error) {
            // If table doesn't exist or column doesn't exist, return empty array
            if (error.code === 'PGRST205' || error.code === 'PGRST204') {
              console.warn('Additional notes templates table or column not found.');
              set({ templates: [] });
              return;
            }
            console.error('Error fetching additional notes templates:', error);
            throw error;
          }
          
          // Transform data to match our interface
          // Database schema: id, title, content, hospital_id, created_at
          const transformedTemplates = data?.map((item: any) => ({
            id: item.id,
            doctorId: doctorId, // Use passed doctorId (for compatibility)
            disease: item.title || '', // Map title to disease for compatibility
            templateName: item.title || '', // Map title to templateName
            notes: item.content || '', // Map content to notes
            createdAt: item.created_at || '',
            updatedAt: item.created_at || '', // Use created_at if updated_at doesn't exist
            usageCount: 0, // Default if column doesn't exist
            lastUsed: undefined
          })) || [];
          
          set({ templates: transformedTemplates });
        } catch (error) {
          console.error('Error in fetchTemplates:', error);
          // Don't throw error, just set empty templates
          set({ templates: [] });
        }
      },
      
      // Clear templates (useful for switching users)
      clearTemplates: () => {
        set({ templates: [] });
      },
    }),
    {
      name: getStorageKey(),
      skipHydration: true,
    }
  )
);

// Save template to backend
export async function saveAdditionalNotesTemplate(templateData: {
  doctorId: string;
  disease: string;
  templateName: string;
  notes: string;
}) {
  try {
    console.log('Saving additional notes template with data:', templateData);
    
    // Get current hospital ID (required by RLS policy)
    const currentHospitalId = await getCurrentHospitalId();
    if (!currentHospitalId) {
      throw new Error('No hospital context available. Please ensure you are logged into a hospital.');
    }

    // Check if template with same title already exists (optional - skip if RLS blocks it)
    // Note: 406 error means RLS policy is blocking the SELECT, but INSERT will still work
    try {
      const { data: existingTemplate, error: checkError } = await supabase
        .from('additional_notes_templates')
        .select('id')
        .eq('hospital_id', currentHospitalId)
        .eq('title', templateData.templateName)
        .maybeSingle(); // Use maybeSingle() instead of single() to handle no results gracefully

      // If we got a result, it's a duplicate
      if (existingTemplate) {
        throw new Error('A template with this name already exists');
      }
      
      // If checkError is 406 (RLS blocking) or other policy errors, continue with insert
      // The duplicate check is optional - the database unique constraint will catch duplicates if needed
      if (checkError && checkError.code !== 'PGRST116') {
        // 406 = RLS policy blocking, PGRST116 = no rows (which is fine)
        // Log only if it's not a policy block or "no rows" error
        if (checkError.status !== 406 && checkError.code !== 'PGRST116') {
          console.log('Duplicate check warning (continuing anyway):', checkError.message);
    }
      }
    } catch (error: any) {
      // If it's our duplicate error, rethrow it so user knows
      if (error.message && error.message.includes('already exists')) {
        throw error;
      }
      // For all other errors (RLS blocking, column not found, etc.), continue with insert
      // The insert will succeed if RLS allows it, and unique constraints will catch duplicates
    }

    // Database auto-generates UUID for id field (don't provide custom ID)
    // Database schema uses: title, content (NOT disease, template_name, notes, doctor_id)
    // Only include columns that exist in the actual database schema
    const insertData = {
      // id is auto-generated by database as UUID (not provided)
      hospital_id: currentHospitalId, // Required by RLS policy
      title: templateData.templateName, // Required NOT NULL - map templateName to title
      content: templateData.notes, // Required NOT NULL - map notes to content
      created_at: new Date().toISOString()
    };

    console.log('Insert data:', insertData);

    const { data, error } = await supabase
      .from('additional_notes_templates')
      .insert([insertData])
      .select()
      .single();

    if (error) {
      // If table doesn't exist, provide helpful error message
      if (error.code === 'PGRST205') {
        throw new Error('Additional notes templates table not found. Please run the database migration first.');
      }
      console.error('Error saving additional notes template:', error);
      throw error;
    }

    return data;
  } catch (error) {
    console.error('Error in saveAdditionalNotesTemplate:', error);
    throw error;
  }
}

// Load template notes
export async function loadAdditionalNotesTemplate(templateId: string): Promise<string> {
  try {
    // Database schema uses 'content' not 'notes', and may not have usage_count
    const { data, error } = await supabase
      .from('additional_notes_templates')
      .select('content, title') // Use 'content' instead of 'notes'
      .eq('id', templateId)
      .single();

    if (error) {
      console.error('Error loading additional notes template:', error);
      throw error;
    }

    // Note: Database schema is minimal (id, title, content, hospital_id, created_at)
    // No need to update usage_count, last_used, or updated_at as they don't exist
    // Template loading is successful, that's what matters

    // Return content (mapped from 'notes' field in our interface)
    return data.content || '';
  } catch (error) {
    console.error('Error in loadAdditionalNotesTemplate:', error);
    throw error;
  }
}

// Delete template
export async function deleteAdditionalNotesTemplate(templateId: string) {
  try {
    const { error } = await supabase
      .from('additional_notes_templates')
      .delete()
      .eq('id', templateId);

    if (error) {
      console.error('Error deleting additional notes template:', error);
      throw error;
    }
  } catch (error) {
    console.error('Error in deleteAdditionalNotesTemplate:', error);
    throw error;
  }
}

// Update template
export async function updateAdditionalNotesTemplate(templateId: string, updates: {
  disease?: string;
  templateName?: string;
  notes?: string;
}) {
  try {
    const updateData: any = {};

    // Map our interface fields to database schema
    // Database uses: title, content (not disease, templateName, notes)
    if (updates.templateName !== undefined) {
      updateData.title = updates.templateName;
    }
    if (updates.notes !== undefined) {
      updateData.content = updates.notes;
    }
    // Note: disease is mapped to title in the database, so if both are provided, templateName takes precedence
    if (updates.disease !== undefined && updates.templateName === undefined) {
      updateData.title = updates.disease;
    }

    // Add updated timestamp if column exists
    updateData.updated_at = new Date().toISOString();

    const { data, error } = await supabase
      .from('additional_notes_templates')
      .update(updateData)
      .eq('id', templateId)
      .select();

    if (error) {
      console.error('Error updating additional notes template:', error);
      throw error;
    }

    return data;
  } catch (error) {
    console.error('Error in updateAdditionalNotesTemplate:', error);
    throw error;
  }
}
